import * as os from 'os';
import * as path from 'path';
import * as fs from 'fs';

interface SystemInfo {
    platform: string;
    arch: string;
    release: string;
    type: string;
    endianness: string;
    cpus: number;
}

interface BinaryInfo {
    name: string;
    path: string;
    exists: boolean;
    executable: boolean;
}

class Arquitecture {
    private systemInfo: SystemInfo;

    constructor() {
        this.systemInfo = this.detectSystemInfo();
    }

    /**
     * Detecta información completa del sistema
     */
    private detectSystemInfo(): SystemInfo {
        return {
            platform: os.platform(),
            arch: os.arch(),
            release: os.release(),
            type: os.type(),
            endianness: os.endianness(),
            cpus: os.cpus().length
        };
    }

    /**
     * Obtiene la plataforma normalizada
     */
    getPlatform(): string {
        const platform = this.systemInfo.platform;
        
        switch (platform) {
            case 'win32':
                return 'windows';
            case 'darwin':
                return 'macos';
            case 'linux':
                return 'linux';
            case 'freebsd':
                return 'freebsd';
            case 'openbsd':
                return 'openbsd';
            case 'sunos':
                return 'solaris';
            default:
                return platform;
        }
    }

    /**
     * Obtiene la arquitectura normalizada
     */
    getArchitecture(): string {
        const arch = this.systemInfo.arch;
        
        switch (arch) {
            case 'x64':
                return 'x86_64';
            case 'x32':
            case 'ia32':
                return 'i686';
            case 'arm64':
                return 'aarch64';
            case 'arm':
                return 'armv7';
            case 'ppc64':
                return 'powerpc64';
            case 'ppc':
                return 'powerpc';
            case 's390x':
                return 's390x';
            case 'mips':
                return 'mips';
            case 'mipsel':
                return 'mipsel';
            default:
                return arch;
        }
    }

    /**
     * Genera el nombre del binario basado en la arquitectura
     */
    getBinaryName(baseName: string): string {
        const platform = this.getPlatform();
        const arch = this.getArchitecture();
        const extension = platform === 'windows' ? '.exe' : '';
        
        return `${baseName}-${platform}-${arch}${extension}`;
    }

    /**
     * Obtiene información completa del sistema
     */
    getSystemInfo(): SystemInfo {
        return { ...this.systemInfo };
    }

    /**
     * Obtiene el triple de destino (target triple) para Rust
     */
    getRustTargetTriple(): string {
        const platform = this.getPlatform();
        const arch = this.getArchitecture();
        
        // Mapeo de plataformas y arquitecturas a target triples de Rust
        const targetMap: { [key: string]: string } = {
            'linux-x86_64': 'x86_64-unknown-linux-gnu',
            'linux-i686': 'i686-unknown-linux-gnu',
            'linux-aarch64': 'aarch64-unknown-linux-gnu',
            'linux-armv7': 'armv7-unknown-linux-gnueabihf',
            'macos-x86_64': 'x86_64-apple-darwin',
            'macos-aarch64': 'aarch64-apple-darwin',
            'windows-x86_64': 'x86_64-pc-windows-msvc',
            'windows-i686': 'i686-pc-windows-msvc',
            'windows-aarch64': 'aarch64-pc-windows-msvc',
            'freebsd-x86_64': 'x86_64-unknown-freebsd',
        };
        
        const key = `${platform}-${arch}`;
        return targetMap[key] || `${arch}-unknown-${platform}`;
    }

    /**
     * Muestra información detallada del sistema
     */
    printSystemInfo(): void {
        console.log('🖥️  System Information:');
        console.log('├─ Platform:', this.getPlatform());
        console.log('├─ Arquitecture:', this.getArchitecture());
        console.log('├─ OS Type:', this.systemInfo.type);
        console.log('├─ OS Release:', this.systemInfo.release);
        console.log('├─ Endianness:', this.systemInfo.endianness);
        console.log('├─ CPUs:', this.systemInfo.cpus);
        console.log('└─ Rust Target:', this.getRustTargetTriple());
    }
}

export { 
    Arquitecture, 
    SystemInfo,
    BinaryInfo
};